import { Chain } from '@1delta/chain-registry'
import { formatEther, formatUnits } from 'viem'

export const BPS = 10000

/**
 * Utilization as a 0..1 fraction. Accepts numeric or numeric-string inputs
 * (raw amounts coming out of `parseRawAmount` are strings). Returns 0 when
 * supply is zero or inputs are invalid.
 */
export const computeUtilization = (
  totalDeposits: number | string,
  totalDebt: number | string,
  totalDebtStable: number | string = 0,
): number => {
  const supply = Number(totalDeposits)
  if (!Number.isFinite(supply) || supply <= 0) return 0
  const debt = Number(totalDebt) + Number(totalDebtStable || 0)
  if (!Number.isFinite(debt) || debt <= 0) return 0
  return debt / supply
}

export const parseRawAmount = (
  answer?: string | bigint,
  decimals = 18,
): string => {
  if (!answer || answer === '0x') return '0'
  try {
    return formatUnits(BigInt(answer ?? '0'), decimals ?? 18)
  } catch (e) {
    return '0'
  }
}

export enum TimeScale {
  BLOCK,
  SECOND,
}

const BLOCK_TIMES: { [chainId: string]: number } = {
  [Chain.ETHEREUM_MAINNET]: 12,
  [Chain.OP_MAINNET]: 1,
  [Chain.BNB_SMART_CHAIN_MAINNET]: 0.75,
  [Chain.ARBITRUM_ONE]: 1,
  [Chain.OPBNB_MAINNET]: 1,
  [Chain.POLYGON_MAINNET]: 2,
  [Chain.BASE]: 1,
  [Chain.CRONOS_MAINNET]: 0.5,
  [Chain.AVALANCHE_C_CHAIN]: 1,
  [Chain.CORE_BLOCKCHAIN_MAINNET]: 3,
}

// Converters from apy to apr and reversed

export const SECONDS_PER_YEAR = 31536000

export const aprToApy = (apr: number) => {
  return (1 + apr / SECONDS_PER_YEAR) ** SECONDS_PER_YEAR - 1
}

export const apyToApr = (apy: number) => {
  return ((apy + 1) ** (1 / SECONDS_PER_YEAR) - 1) * SECONDS_PER_YEAR
}

export const apyToAprPercent = (apy: number) => {
  return (
    100 * ((apy / 100 + 1) ** (1 / SECONDS_PER_YEAR) - 1) * SECONDS_PER_YEAR
  )
}

export const formatAaveYieldToNumber = (n?: string | bigint): number => {
  if (!n) return 0
  return Number(formatEther(BigInt(n) / BigInt(1e7)))
}

/**
 * Lodestar / Tender variant: hardcodes 12 s per unit.
 *
 * That constant is NOT Ethereum's block time leaking in — it is correct, and
 * for a reason worth writing down: these markets live on Arbitrum, where a
 * contract's `block.number` is an L1 block number, so one unit really is ~12 s
 * even though the L2 produces a block every 0.25 s. `unitSeconds` overrides it
 * once the clock has been resolved from the chain.
 */
export const calculateRateForCompoundType2 = (
  n: string,
  unitSeconds?: number,
): number => {
  const rate = Number(formatEther(BigInt(n)))
  const perUnit = unitSeconds && unitSeconds > 0 ? unitSeconds : 12
  return (Math.pow((rate * 60 * 60 * 24) / perUnit + 1, 365) - 1) * 100
}

/**
 * Annualize a Compound V2 style per-unit rate.
 *
 * `unitSeconds` is how many seconds ONE unit of the rate represents, resolved
 * per market from the chain (`compound-v2/rateClock.ts`). Pass it whenever it
 * is known — the `BLOCK_TIMES` fallback below is a per-CHAIN guess and cannot
 * be right for every fork on a chain:
 *
 *  - forks differ WITHIN a chain (Sonne counts seconds, Cream counts blocks,
 *    both on the same table entry);
 *  - Arbitrum contracts count L1 blocks (~12 s) while the chain produces an L2
 *    block every 0.25 s;
 *  - a chain's cadence DRIFTS — BSC went 3 s → 0.75 s → 0.45 s, and every
 *    stale entry silently mis-states every rate on it.
 *
 * The table stays as the fallback so an unresolved probe degrades to the old
 * behaviour instead of to a guess.
 */
export const calculateRateForCompoundType = (
  n: string,
  chainId: string,
  scale = TimeScale.SECOND,
  unitSeconds?: number,
): number => {
  const rate = Number(formatEther(BigInt(n)))
  const perUnit =
    unitSeconds && unitSeconds > 0
      ? unitSeconds
      : scale === TimeScale.BLOCK
        ? Number(BLOCK_TIMES[chainId] ?? 1)
        : 1
  return (Math.pow((rate * 60 * 60 * 24) / perUnit + 1, 365) - 1) * 100
}

export const formatAaveRawApyToApr = (raw: bigint | string | undefined) => {
  const apy = formatAaveYieldToNumber(raw)
  return apyToApr(apy / 100) * 100
}

// converts rate per second to rate per year
export const convertRateToApr = (ratePerSecond: number | string) => {
  const rps = Number(ratePerSecond)
  if (isNaN(rps)) return 0
  return rps * 3600 * 24 * 365 * 100
}

export const formatAavePrice = (price: string, exponent = 10n): number => {
  try {
    return Number(
      formatEther(
        BigInt(price ?? '0') *
          // 10^(18 - decimals)
          10n ** exponent,
      ),
    )
  } catch {
    return NaN
  }
}

export const formatMorphoPrice = (
  price: string,
  collateralDec: number,
  debtDec: number,
): number => {
  try {
    return Number(
      formatUnits(
        BigInt(price ?? '0'),
        // 10^(18 - decimals)
        36 + collateralDec - debtDec,
      ),
    )
  } catch {
    return NaN
  }
}
